import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Archive, Check, Copy, Eye, RefreshCw } from "lucide-react";

import { AdminScreen, Chips, Field, ListEditor, OPTS, Section, Toggle, UploadButton } from "@/admin/kit";
import { api, relUrl } from "@/lib/api";
import { Button, Spinner } from "@/components/kit";
import { useApp } from "@/store";

const KEYS = ["name", "description", "image_url", "family", "suitable_hair_types", "min_length", "difficulty", "maintenance", "required_tools", "styling_steps",
  "styling_tips", "primary_product", "supporting_product", "hold", "finish", "tags", "styling_time", "face_shapes", "categories", "video_url",
  "reason_template", "amount_note", "face_note", "archived"];
const LISTS = ["suitable_hair_types", "required_tools", "styling_steps", "styling_tips", "tags", "face_shapes", "categories"];

export default function AdminHairstyle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { toast, refreshRecs } = useApp();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setD(null);
    api(`/admin/hairstyles/${slug}`)
      .then((s) => setD(Object.fromEntries(KEYS.map((k) => [k, s[k] ?? (k === "archived" ? false : LISTS.includes(k) ? [] : "")]))))
      .catch((e) => toast(e.message, "error"));
  }, [slug, toast]);
  if (!d) return <AdminScreen title="Hairstyle" testId="admin-style-editor" fallback="/admin/hairstyles"><Spinner /></AdminScreen>;
  const set = (k) => (v) => setD((x) => ({ ...x, [k]: v }));
  const one = (k) => (v) => set(k)(v[0]);
  const id = "admin-style";

  const save = async (extra = {}) => {
    setBusy(true);
    try {
      const body = { ...d, ...extra, styling_time: Number(d.styling_time) || 5, image_url: relUrl(d.image_url || ""), video_url: relUrl((d.video_url || "").trim()),
        supporting_product: d.supporting_product || null, styling_steps: d.styling_steps.filter((x) => x.trim()),
        required_tools: d.required_tools.filter((x) => x.trim()), styling_tips: d.styling_tips.filter((x) => x.trim()) };
      const s = await api(`/admin/hairstyles/${slug}`, { method: "PUT", body });
      setD((x) => ({ ...x, ...extra, archived: s.archived }));
      refreshRecs();
      toast("Saved — live in the app now.", "success");
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setBusy(false);
    }
  };
  const duplicate = async () => {
    try {
      const s = await api(`/admin/hairstyles/${slug}/duplicate`, { method: "POST" });
      navigate(`/admin/hairstyle/${s.slug}`, { replace: true });
    } catch (e) {
      toast(e.message, "error");
    }
  };
  const validVideo = !d.video_url || /^(\/api\/files\/|https?:\/\/)/.test(relUrl(d.video_url.trim()));
  const sm = "!min-h-[44px]";

  return (
    <AdminScreen title={d.name || slug} testId="admin-style-editor" fallback="/admin/hairstyles" right={<Button testId={`${id}-save-top`} title="Save" icon={Check} loading={busy} disabled={!validVideo} onClick={() => save()} className={`${sm} !px-4`} />}>
      <div className="flex flex-wrap gap-2">
        <Button testId={`${id}-duplicate`} title="Duplicate" icon={Copy} variant="outline" onClick={duplicate} className={sm} />
        <Button testId={`${id}-archive`} title={d.archived ? "Restore" : "Archive"} icon={d.archived ? RefreshCw : Archive} variant="outline" onClick={() => save({ archived: !d.archived })} className={sm} />
        <Button testId={`${id}-view`} title="View in app" icon={Eye} variant="ghost" onClick={() => navigate(`/style/${slug}`)} className={sm} />
      </div>
      {d.archived ? <p data-testid={`${id}-archived-note`} className="t-strong">Archived — hidden from users.</p> : null}
      <div className="grid items-start gap-5 md:grid-cols-2">
        <div className="flex flex-col gap-5">
          <Section title="Basics">
            <Field testId={`${id}-name`} label="Name" value={d.name} onChange={set("name")} />
            <Field testId={`${id}-description`} label="Description" value={d.description} onChange={set("description")} multiline />
            <div className="flex flex-col gap-2">
              <span className="t-label">Reference image</span>
              {d.image_url ? <img data-testid={`${id}-image`} src={d.image_url} alt={d.name} className="h-[150px] w-[120px] rounded-xl object-cover" /> : null}
              <UploadButton kind="image" testId={`${id}-image-upload`} title={d.image_url ? "Replace image" : "Upload image"} onUploaded={(u) => set("image_url")(u.url)} onError={(m) => toast(m, "error")} />
            </div>
            <Chips testId={`${id}-family`} single label="Family" options={OPTS.families} value={[d.family]} onChange={one("family")} />
          </Section>
          <Section title="Who it suits">
            <Chips testId={`${id}-types`} label="Hair patterns / types" options={OPTS.hair_types} value={d.suitable_hair_types} onChange={set("suitable_hair_types")} />
            <Chips testId={`${id}-length`} single label="Minimum length" options={OPTS.lengths} value={[d.min_length]} onChange={one("min_length")} />
            <Chips testId={`${id}-faces`} label="Face shapes" options={OPTS.faces} value={d.face_shapes} onChange={set("face_shapes")} />
            <Field testId={`${id}-face-note`} label="Face-shape notes (optional)" value={d.face_note} onChange={set("face_note")} multiline />
            <Chips testId={`${id}-difficulty`} single label="Difficulty" options={OPTS.difficulty} value={[d.difficulty]} onChange={one("difficulty")} />
            <Chips testId={`${id}-maintenance`} single label="Maintenance" options={OPTS.maintenance} value={[d.maintenance]} onChange={one("maintenance")} />
            <Chips testId={`${id}-hold`} single label="Hold" options={OPTS.hold} value={[d.hold]} onChange={one("hold")} />
            <Chips testId={`${id}-finish`} single label="Finish" options={OPTS.finish} value={[d.finish]} onChange={one("finish")} />
            <Chips testId={`${id}-categories`} label="Categories" options={OPTS.categories} value={d.categories} onChange={set("categories")} />
            <Chips testId={`${id}-tags`} label="Tags" options={OPTS.tags} value={d.tags} onChange={set("tags")} />
          </Section>
        </div>
        <div className="flex flex-col gap-5">
          <Section title="BREEZ product match (rules)" note="Template placeholders: {product}, {style}, {hair_type}. Leave blank to use the default reason.">
            <Chips testId={`${id}-primary`} single label="Primary product" options={OPTS.products} value={[d.primary_product]} onChange={one("primary_product")} />
            <Chips testId={`${id}-supporting`} single label="Supporting product (optional)" options={["none", ...OPTS.products]} value={[d.supporting_product || "none"]} onChange={(v) => set("supporting_product")(v[0] === "none" ? null : v[0])} />
            <Field testId={`${id}-reason`} label="Reason text template" value={d.reason_template} onChange={set("reason_template")} multiline placeholder="We recommend {product} because…" />
            <Field testId={`${id}-amount`} label="How much to start with (blank = product default)" value={d.amount_note} onChange={set("amount_note")} />
            <Field testId={`${id}-time`} label="Styling time (minutes)" value={String(d.styling_time)} onChange={set("styling_time")} numeric />
          </Section>
          <Section title="Tutorial · STYLE IT.">
            <ListEditor testId={`${id}-steps`} label="Steps" items={d.styling_steps} onChange={set("styling_steps")} numbered />
            <ListEditor testId={`${id}-tools`} label="Tools" items={d.required_tools} onChange={set("required_tools")} />
            <ListEditor testId={`${id}-tips`} label="Styling tips" items={d.styling_tips} onChange={set("styling_tips")} />
          </Section>
          <Section title="Demo video" note="Upload an MP4/MOV (≤200MB) or paste a YouTube/Vimeo link.">
            <Field testId={`${id}-video-url`} label="Video URL" value={d.video_url} onChange={set("video_url")} placeholder="https://youtu.be/…" />
            {!validVideo ? <p data-testid={`${id}-video-invalid`} className="t-caption !text-[#EF4444]">Enter a full https:// link.</p> : null}
            <div className="flex flex-wrap gap-2">
              <UploadButton kind="video" testId={`${id}-video-upload`} title="Upload video" onUploaded={(u) => { set("video_url")(u.url); toast("Video uploaded — click Save to attach it.", "success"); }} onError={(m) => toast(m, "error")} />
              {d.video_url ? <Button testId={`${id}-video-clear`} title="Remove video" variant="ghost" onClick={() => set("video_url")("")} className={sm} /> : null}
            </div>
            <Toggle testId={`${id}-archived`} label="Archived (hidden from users)" value={!!d.archived} onChange={set("archived")} />
          </Section>
        </div>
      </div>
      <Button testId={`${id}-save`} title="Save hairstyle" icon={Check} loading={busy} disabled={!validVideo} onClick={() => save()} />
    </AdminScreen>
  );
}
